import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  ExerciseConstructorContent,
  WordsComparingExerciseContent,
} from "content";
import { useExerciseConstructor } from "hooks/redux/useExerciseConstructor";
import {
  useAddExercise,
  useAddHomeworkExercise,
  useEditExercise,
  useEditHomeworkExercise,
} from "hooks/api/exercises";
import { Space } from "components/Common/Space";
import { Textarea } from "components/Interactive/Textarea";
import { PairWordsInput } from "components/Interactive/PairWordsInput";
import { Divider } from "components/Common/Divider";
import { ExerciseConfigForm } from "../../ExerciseConfigForm";
import { logError, logSuccess } from "utils/notifications";
import {
  adaptWordsToExerciseData,
  getWordsComparingExerciseFormInitialValues,
} from "./WordsComparingExerciseForm.helpers";
import { TAddExercise, TEditExercise } from "types/api/exercise";
import {
  EExerciseType,
  TWordsComparingExercisePayload,
} from "types/app/exercises";
import { TWordsComparingExerciseForm } from "./WordsComparingExerciseForm.types";
import "./WordsComparingExerciseForm.styles.scss";

export const WordsComparingExerciseForm: React.FC = () => {
  const {
    courseId,
    lessonId,
    chapterId,
    homeworkId,
    editExercise,
    isEditMode,
    constructorMode,
    closeAll,
  } = useExerciseConstructor<TWordsComparingExercisePayload>();
  const { mutate: addExercise } =
    useAddExercise<TWordsComparingExercisePayload>(
      courseId,
      lessonId,
      chapterId
    );
  const { mutate: addHomeworkExercise } =
    useAddHomeworkExercise<TWordsComparingExercisePayload>(
      courseId,
      lessonId,
      homeworkId
    );
  const { mutate: editExerciseHandler } =
    useEditExercise<TWordsComparingExercisePayload>(
      courseId,
      lessonId,
      chapterId
    );
  const { mutate: editHomeworkExerciseHandler } =
    useEditHomeworkExercise<TWordsComparingExercisePayload>(
      courseId,
      lessonId,
      homeworkId
    );

  const defaultValues: Partial<TWordsComparingExerciseForm> = useMemo(
    () => getWordsComparingExerciseFormInitialValues(editExercise),
    [editExercise]
  );

  const {
    handleSubmit,
    control,
    register,
    reset,
    formState: { errors },
  } = useForm<TWordsComparingExerciseForm>({
    mode: "onBlur",
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const successCallback = () => {
    logSuccess(
      isEditMode
        ? ExerciseConstructorContent.Notification.Edit.SUCCESS
        : ExerciseConstructorContent.Notification.Add.SUCCESS
    );
    closeAll();
    reset(defaultValues);
  };

  const submitLessonExercise = (
    body: TAddExercise<TWordsComparingExercisePayload>
  ) => {
    addExercise(body, {
      onSuccess: successCallback,
    });
  };

  const submitHomeworkExercise = (
    body: TAddExercise<TWordsComparingExercisePayload>
  ) => {
    addHomeworkExercise(body, {
      onSuccess: successCallback,
    });
  };

  const submitEditLessonExercise = (
    body: TEditExercise<TWordsComparingExercisePayload>
  ) => {
    editExerciseHandler(body, {
      onSuccess: successCallback,
    });
  };

  const submitEditHomeworkExercise = (
    body: TEditExercise<TWordsComparingExercisePayload>
  ) => {
    editHomeworkExerciseHandler(body, {
      onSuccess: successCallback,
    });
  };

  const exerciseSubmitHandler = (formData: TWordsComparingExerciseForm) => {
    const isExerciseParamsInvalid =
      !courseId || !lessonId || !(chapterId || homeworkId);
    if (isExerciseParamsInvalid) {
      logError({
        message: ExerciseConstructorContent.Notification.Add.ERROR,
      });
      return;
    }

    const parsedData = adaptWordsToExerciseData(formData.words);

    const addBody: TAddExercise<TWordsComparingExercisePayload> = {
      type: EExerciseType.WORDS_COMPARING,
      payload: {
        text: formData.text,
        data: parsedData,
      },
    };

    if (!isEditMode && constructorMode === "lesson") {
      return submitLessonExercise(addBody);
    }

    if (!isEditMode && constructorMode === "homework") {
      return submitHomeworkExercise(addBody);
    }

    if (isEditMode && editExercise) {
      const editBody: TEditExercise<TWordsComparingExercisePayload> = {
        id: editExercise.id,
        ...addBody,
      };

      if (constructorMode === "lesson") {
        return submitEditLessonExercise(editBody);
      }

      if (constructorMode === "homework") {
        return submitEditHomeworkExercise(editBody);
      }
    }
  };

  return (
    <ExerciseConfigForm
      onSubmit={handleSubmit(exerciseSubmitHandler)}
      isEditMode={isEditMode}
    >
      <Space direction="vertical" justify="start" size="medium" fullWidth>
        <Textarea<TWordsComparingExerciseForm>
          name="text"
          id="words-comparing-exercise-condition"
          className="nb-exercise-constructor-words-comparing-condition"
          label={WordsComparingExerciseContent.Add.Input.Condition.LABEL}
          placeholder={
            WordsComparingExerciseContent.Add.Input.Condition.PLACEHOLDER
          }
          register={register}
          errors={errors}
          required
          fullWidth
        />
        <Divider />
        <PairWordsInput<TWordsComparingExerciseForm>
          name="words"
          control={control}
          register={register}
          errors={errors}
          addPairButtonText={
            WordsComparingExerciseContent.Add.Input.Words.ADD_PAIR_BUTTON
          }
          label={WordsComparingExerciseContent.Add.Input.Words.LABEL}
          wordPlaceholder={
            WordsComparingExerciseContent.Add.Input.Words.WORD_PLACEHOLDER
          }
          linkWordPlaceholder={
            WordsComparingExerciseContent.Add.Input.Words.LINK_WORD_PLACEHOLDER
          }
        />
      </Space>
    </ExerciseConfigForm>
  );
};
